import React, { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import { LayOut } from "pages/layout/LayOut";
import Logo from "components/ui/Logo";
import ErrorBoundary from "components/ErrorBoundary";
// Remove border and things for that
// ─── Eager (small + on the critical path) ───────────────────
import Homepage from "pages/homepage";

// ─── Lazy (everything else) ─────────────────────────────────
// Each becomes its own JS chunk; the user only downloads the page they
// actually visit. Cuts the initial JS by ~60% in practice.
const UserRegistrationLogin         = lazy(() => import("pages/user-registration-login"));
const NotFound                      = lazy(() => import("pages/NotFound"));
const AboutUs                       = lazy(() => import("pages/about-us"));
const AgreementsHub                 = lazy(() => import("pages/agreements"));
const AgreementPage                 = lazy(() => import("pages/agreements/AgreementPage"));
const RentHub                       = lazy(() => import("pages/rentals"));
const RentLanding                   = lazy(() => import("pages/rentals/RentLanding"));
const AdvancedSearchDiscovery       = lazy(() => import("pages/advanced-search-discovery"));
const AdminDashboard                = lazy(() => import("pages/admin-dashboard"));
const Blog                          = lazy(() => import("pages/blog"));
const BlogPost                      = lazy(() => import("pages/blog/BlogPost"));
const ContactUs                     = lazy(() => import("pages/contact-us"));
const Favorites                     = lazy(() => import("pages/favorites"));
const SafetyGuidelines              = lazy(() => import("pages/guidelines/SafetyGuidelines"));
const HelpCenter                    = lazy(() => import("pages/help-center"));
const LegalResources                = lazy(() => import("pages/legal-resources"));
const MessagesPage                  = lazy(() => import("pages/messages-communication"));
const MyBookings                    = lazy(() => import("pages/my-bookings"));
const NotificationsPage             = lazy(() => import("pages/notifications"));
const OwnerDashboardSuite           = lazy(() => import("pages/owner-dashboard-suite"));
const PrivacyPolicy                 = lazy(() => import("pages/privacy-policy"));
const PropertyDetailExperience      = lazy(() => import("pages/property-detail-experience"));
const PropertyListingCreation       = lazy(() => import("pages/property-listing-creation"));
const TermsOfService                = lazy(() => import("pages/terms-of-service"));
const UserProfileVerificationCenter = lazy(() => import("pages/user-profile-verification-center"));

// Lightweight shell shown while a route's chunk is downloading.
const RouteSuspenseFallback = () => (
  <div className="min-h-screen flex flex-col items-center justify-center pt-16 px-4 text-center">
    <Logo size={36} to={null} />
    <div className="mt-6 inline-block w-6 h-6 rounded-full border-2 border-rose-500 border-t-transparent animate-spin" />
    <p className="mt-3 text-sm text-gray-500">Loading…</p>
  </div>
);

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
      <Suspense fallback={<RouteSuspenseFallback />}>
        <Routes>
          <Route element={<LayOut />}>
            {/* Public */}
            <Route path="/" element={<Homepage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Agreements (city + category, SEO landing pages) */}
            <Route path="/agreements" element={<AgreementsHub />} />
            <Route path="/rent-agreement" element={<AgreementPage type="rent" />} />
            <Route path="/rent-agreement/:city" element={<AgreementPage type="rent" />} />
            <Route path="/lease-agreement" element={<AgreementPage type="lease" />} />
            <Route path="/lease-agreement/:city" element={<AgreementPage type="lease" />} />
            <Route path="/buy-agreement" element={<AgreementPage type="buy" />} />
            <Route path="/buy-agreement/:city" element={<AgreementPage type="buy" />} />
            <Route path="/sell-agreement" element={<AgreementPage type="sell" />} />
            <Route path="/sell-agreement/:city" element={<AgreementPage type="sell" />} />

            {/* City + type rent landing pages (SEO) */}
            <Route path="/rent" element={<RentHub />} />
            <Route path="/rent/:city" element={<RentLanding />} />
            <Route path="/rent/:city/:type" element={<RentLanding />} />
            <Route path="/advanced-search-discovery" element={<AdvancedSearchDiscovery />} />
            <Route path="/p/:id" element={<PropertyDetailExperience />} />
            <Route path="/to-let/:city/:locality/:slug" element={<PropertyDetailExperience />} />
            <Route path="/property-detail-experience" element={<PropertyDetailExperience />} />
            <Route path="/property-detail-experience/:id" element={<PropertyDetailExperience />} />
            <Route path="/user-registration-login" element={<UserRegistrationLogin />} />

            {/* Authenticated */}
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/my-bookings" element={<MyBookings />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/owner-dashboard-suite" element={<OwnerDashboardSuite />} />
            <Route path="/user-profile-verification-center" element={<UserProfileVerificationCenter />} />
            <Route path="/messages-communication" element={<MessagesPage />} />
            <Route path="/property-listing" element={<PropertyListingCreation />} />
            <Route path="/property-listing-creation" element={<PropertyListingCreation />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />

            {/* Support & legal */}
            <Route path="/help-center" element={<HelpCenter />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/guidelines" element={<SafetyGuidelines />} />
            <Route path="/legal" element={<LegalResources />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRoutes;
